import React, { useEffect, useRef, useState } from 'react';
import KoobTitle from '../../../../components/Koob/KoobTitle';
import KoobProgressBar from '../../../../components/Koob/KoobProgressBar';
import { KoobButton, KoobCard } from '@koob/ui';
import { Trans, useTranslation } from 'react-i18next';
import {
  ExperienceTypology,
  getSchema as getTypologySchema,
} from '../Steps/ExperienceTypology';
import {
  ExperienceConfig,
  getSchema as getConfigSchema,
} from '../Steps/ExperienceConfig';
import {
  ExperienceActivities,
  getSchema as getActivitySchema,
} from '../Steps/ExperienceActivities';
import {
  ExperienceItinerary,
  getSchema as getItinerarySchema,
} from '../Steps/ExperienceItinerary';
import {
  ExperienceProgram,
  getSchema as getProgramSchema,
} from '../Steps/ExperienceProgram';
import {
  ExperienceTransferConfig,
  getSchema as getTransferConfigSchema,
} from '../Steps/Transfer/ExperienceTransferConfig';
import {
  ExperienceTransferSpecification,
  getSchema as getTransferSpecSchema,
} from '../Steps/Transfer/ExperienceTransferSpecification';
import {
  ExperienceExtrasAndActivities,
  getSchema as getServiceSchema,
} from '../Steps/ExperienceExtrasAndActivities';
import { SelectField } from '../../../../components/Fields';
import {
  ExperiencePricing,
  getSchema as getPricingSchema,
} from '../Steps/ExperiencePricing';
import { Formik } from 'formik';
import { formatExperienceApiData } from '../Utilities/ExperienceHelpers';
import {
  ExperienceMedia,
  getSchema as getMediaSchema,
} from '../Steps/ExperienceMedia';
import { useNavigate } from 'react-router-dom';
import ExperienceHeader from './ExperienceHeader';
import {
  ExperienceOptions,
  getSchema as getOptionsSchema,
} from '../Steps/ExperienceOptions';
import { LocaleSwitcher } from '../../../../components';
import {
  ExperienceCondition,
  getSchema as getConditionSchema,
} from '../Steps/ExperienceCondition';
import {
  ExperienceHotelRoomConfig,
  getSchema as getAccommodationSchema,
} from '../Steps/ExperienceHotelRoomConfig';
import { useApp } from '../../../../hooks';
import { ExperienceTypeEnum, RoleEnum } from '@koob/enums';
import { KoobAlert } from '@koob/ui';

export default function ExperiencesWizard({
  initialExperience,
  initialStep,
  handleSave,
  currentLocale,
  setCurrentLocale,
  organization,
  isReadOnly,
}) {
  const { t } = useTranslation('experiences');
  const { user } = useApp();
  const navigate = useNavigate();
  const [currentValidationSchema, setCurrentValidationSchema] = useState();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [maxCurrentStep, setMaxCurrentStep] = useState(initialStep);
  const [experienceToImport, setExperienceToImport] = useState();

  const setNewCurrentStep = (step) => {
    setMaxCurrentStep(Math.max(Math.max(maxCurrentStep, currentStep), step));
    setCurrentStep(step);
  };

  const formRef = useRef();
  useEffect(() => {
    formRef.current.validateForm();
  }, [currentValidationSchema, formRef]);

  const onSelectExperience = (exp) => {
    const expToImport = {
      ...exp,
      pictures: exp.pictures?.filter((pic) => !pic.isDayPicture),
      programs: exp.programs.map((program) => {
        return {
          ...program,
          id: undefined,
          pois: program.pois.map((poi) => {
            return {
              ...poi,
              id: undefined,
            };
          }),
        };
      }),
    };
    const formatted = formatExperienceApiData(expToImport, true);
    // Preserve the source experience id so backend can track the import origin.
    setExperienceToImport({
      ...formatted,
      importFromExperienceId: exp?.id ? String(exp.id) : undefined,
    });
    setCurrentStep(1);
  };

  const initialValues = experienceToImport ?? initialExperience;

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={currentValidationSchema}
        onSubmit={(values) => {
          return handleSave(values);
        }}
        context={{ activity: initialValues.activity }}
        validateOnMount={true}
        innerRef={formRef}
        enableReinitialize={true}
      >
        {({ isValid, values, submitForm }) => {
          const steps = [
            {
              name: t('steps.typology.name'),
              title: t('steps.typology.title'),
              description: t('steps.typology.description'),
              component: (
                <ExperienceTypology onSelectExperience={onSelectExperience} />
              ),
              schema: getTypologySchema(),
              hidden: true,
              disabled: true,
            },

            ...(values.type === ExperienceTypeEnum.ACTIVITY &&
            !initialExperience.id
              ? [
                  {
                    name: t('steps.extraAndActivity.name'),
                    title: t('steps.extraAndActivity.title'),
                    description: t('steps.extraAndActivity.description'),
                    schema: getServiceSchema(),
                    component: <ExperienceExtrasAndActivities />,
                    disabled: true,
                  },
                ]
              : []),
            {
              name: t('steps.general.name'),
              title: t('steps.general.title'),
              description: t('steps.general.description'),
              component: <ExperienceConfig isReadOnly={isReadOnly} />,
              schema: getConfigSchema(),
            },
            ...(values.type === ExperienceTypeEnum.TRANSFER
              ? [
                  {
                    name: t('steps.transferConfig.name'),
                    title: t('steps.transferConfig.title'),
                    description: t('steps.transferConfig.description'),
                    component: <ExperienceTransferConfig />,
                    schema: getTransferConfigSchema(),
                  },
                  {
                    name: t('steps.transferSpecification.name'),
                    title: t('steps.transferSpecification.title'),
                    description: t('steps.transferSpecification.description'),
                    component: <ExperienceTransferSpecification />,
                    schema: getTransferSpecSchema(values),
                  },
                ]
              : []),
            ...(values?.type === ExperienceTypeEnum.ACTIVITY &&
            !values?.activity?.isExtra
              ? [
                  {
                    name: t('steps.activity.name'),
                    title: t('steps.activity.title'),
                    description: t('steps.activity.description'),
                    schema: getActivitySchema(),
                    component: <ExperienceActivities isReadOnly={isReadOnly} />,
                  },
                ]
              : []),
            ...(values.type === ExperienceTypeEnum.PROGRAM
              ? [
                  {
                    name: t('steps.program.name'),
                    title: t('steps.program.title'),
                    description: t('steps.program.description'),
                    schema: getProgramSchema(),
                    component: <ExperienceProgram isReadOnly={isReadOnly} />,
                  },
                  {
                    name: t('steps.itinerary.name'),
                    title: t('steps.itinerary.title'),
                    description: t('steps.itinerary.descriptions'),
                    schema: getItinerarySchema(),
                    component: (
                      <ExperienceItinerary
                        currentLocale={currentLocale}
                        isReadOnly={isReadOnly}
                      />
                    ),
                  },
                ]
              : []),
            {
              name: t('steps.media.name'),
              title: t('steps.media.title'),
              description: t('steps.media.description'),
              component: (
                <ExperienceMedia
                  currentLocale={currentLocale}
                  isReadOnly={isReadOnly}
                />
              ),
              schema: getMediaSchema(values),
            },
            {
              name: t('steps.pricing.name'),
              title: t('steps.pricing.title'),
              description: t('steps.pricing.description'),
              component: (
                <ExperiencePricing
                  currentLocale={currentLocale}
                  values={values}
                  organization={organization}
                  isReadOnly={isReadOnly}
                />
              ),
              schema: getPricingSchema(values),
            },
            ...(values.type === ExperienceTypeEnum.PROGRAM
              ? [
                  {
                    name: t('steps.accommodation.name'),
                    title: t('steps.accommodation.title'),
                    description: t('steps.accommodation.description'),
                    schema: getAccommodationSchema(),
                    component: (
                      <ExperienceHotelRoomConfig isReadOnly={isReadOnly} />
                    ),
                  },
                ]
              : []),
            ...(!values?.activity?.isExtra
              ? [
                  {
                    name: t('steps.condition.name'),
                    title: t('steps.condition.title'),
                    description: t('steps.condition.description'),
                    component: <ExperienceCondition isReadOnly={isReadOnly} />,
                    schema: getConditionSchema(),
                  },
                ]
              : []),
            {
              name: t('steps.options.name'),
              title: t('steps.options.title'),
              description: t('steps.options.description'),
              component: <ExperienceOptions currentLocale={currentLocale} />,
              schema: getOptionsSchema(),
            },
          ];
          values?.type &&
            setCurrentValidationSchema(steps[currentStep]?.schema);
          let experienceFullValid = true;
          steps.forEach((step) => {
            if (step.schema) {
              experienceFullValid =
                experienceFullValid && step.schema.isValidSync(values);
            }
          });
          return (
            <>
              {currentStep > 0 && (
                <>
                  <ExperienceHeader
                    currentLocale={currentLocale}
                    showExportOptions
                    values={values}
                    rightElement={
                      <SelectField
                        label={t('state.title')}
                        name="state"
                        valueField={'value'}
                        options={[
                          { label: t('state.available'), value: 'available' },
                          {
                            label: t('state.inProgress'),
                            value: 'in_progress',
                          },
                          { label: t('state.standBy'), value: 'stand_by' },
                        ]}
                        testId="experience-status"
                        disabled={isReadOnly}
                      />
                    }
                  />

                  <KoobCard style={{ marginBottom: '1.25rem' }}>
                    <div className="mb-2 flex justify-between">
                      {steps.map(
                        (step, index) =>
                          !step.hidden && (
                            <div className="text-center" key={index}>
                              {isValid &&
                              !step.disabled &&
                              (index <= maxCurrentStep ||
                                (initialExperience.id &&
                                  index !== 0 &&
                                  (!steps[index - 1].schema ||
                                    steps[index - 1].schema.isValidSync(
                                      values,
                                    )))) ? (
                                <button
                                  key={index}
                                  onClick={() => setNewCurrentStep(index)}
                                  className="border-none font-medium"
                                  data-testid={step.name?.toLowerCase()}
                                >
                                  {step.name}
                                </button>
                              ) : (
                                <div className="font-medium opacity-75 cursor-not-allowed">
                                  {step.name}
                                </div>
                              )}
                            </div>
                          ),
                      )}
                    </div>
                    <KoobProgressBar
                      percentage={(currentStep / (steps.length - 1)) * 100}
                    />
                  </KoobCard>
                </>
              )}

              {user.role === RoleEnum.KOOB_ADMIN && (
                <KoobAlert
                  icon="fa-lock"
                  title="Editing disabled for KOOB admins"
                  description="Please impersonate the relevant user to edit this experience."
                  variant="warning"
                />
              )}
              {user.role === RoleEnum.DMC_ADMIN && isReadOnly && (
                <KoobAlert
                  icon="fa-lock"
                  title={t('experiences:form:editionAlert')}
                  description={
                    <Trans
                      i18nKey="experiences:form:partnerEditionAlert"
                      values={{ dmc: initialExperience.organizationName }}
                      components={{ bold: <strong /> }}
                    />
                  }
                  variant="warning"
                />
              )}
              <>
                <KoobCard>
                  <div className="flex justify-center relative pb-5 mb-2 border-b border-gray-100">
                    <div className="text-center">
                      <KoobTitle size="text-2xl">
                        {steps[currentStep]?.title}
                      </KoobTitle>
                      <div className="text-sm font-medium text-gray-400 mt-1">
                        {steps[currentStep]?.description}
                      </div>
                    </div>

                    {initialExperience.id && (
                      <div className="absolute top-0 right-0">
                        <LocaleSwitcher
                          queryLocale={currentLocale}
                          updateQueryLocale={setCurrentLocale}
                        />
                      </div>
                    )}
                  </div>

                  <div>{steps[currentStep]?.component}</div>

                  <div className="mt-8 mb-2 flex items-center justify-between pt-5 border-t border-gray-100 px-4">
                    <KoobButton
                      variant="primary"
                      onClick={() => setNewCurrentStep(currentStep - 1)}
                      disabled={
                        steps[currentStep - 1]?.disabled ||
                        !isValid ||
                        currentStep === 0
                      }
                    >
                      {t('misc:previous')}
                    </KoobButton>
                    <KoobButton
                      variant="primary"
                      disabled={
                        !isValid ||
                        user.role === RoleEnum.KOOB_ADMIN ||
                        currentStep === 0 ||
                        (!initialExperience.id &&
                          currentStep === 1 &&
                          values?.type === 'Activity') ||
                        (values.state === 'available' && !experienceFullValid)
                      }
                      testId="save-experience"
                      onClick={() => {
                        submitForm().then((id) => {
                          if (!values?.id && id) {
                            navigate(
                              `/experiences/edit/${id}?step=${
                                values?.type === 'Activity'
                                  ? currentStep - 1
                                  : currentStep
                              }`,
                              {
                                replace: true,
                              },
                            );
                          }
                        });
                      }}
                    >
                      {t('misc:save')}
                    </KoobButton>
                    {currentStep < steps.length - 1 && (
                      <KoobButton
                        variant="primary"
                        onClick={() => setNewCurrentStep(currentStep + 1)}
                        disabled={currentStep === steps.length - 1 || !isValid}
                      >
                        {t('misc:next')}
                      </KoobButton>
                    )}
                    {currentStep === steps.length - 1 && (
                      <KoobButton
                        variant="primary"
                        disabled={
                          !isValid ||
                          user.role === RoleEnum.KOOB_ADMIN ||
                          currentStep === 0 ||
                          (values.state === 'available' &&
                            currentStep !== steps.length - 1)
                        }
                        onClick={() => {
                          submitForm().then(() =>
                            navigate('/experiences', { replace: true }),
                          );
                        }}
                      >
                        {t('misc:finish')}
                      </KoobButton>
                    )}
                  </div>
                </KoobCard>
              </>
            </>
          );
        }}
      </Formik>
    </>
  );
}
